import { Fragment } from 'react'

interface StoryTitleSegment {
  text: string
  highlight?: boolean
}

interface StoryBlock {
  titleSegments: StoryTitleSegment[]
  description: string
  closing: string
}

interface StorySectionProps {
  blocks: StoryBlock[]
  imageUrl: string
  imageUrlMobile: string
  imageAlt: string
}

function TextBlock({ block }: { block: StoryBlock }) {
  // Figma mobile (node 194:3662) stacks + centers all text; desktop (node 37:10146)
  // splits into 2 columns (title left / body right) with left-aligned text.
  return (
    <div className="w-full max-w-[1136px] mx-auto px-6 lg:px-0 flex flex-col lg:flex-row items-center lg:items-start gap-8 lg:gap-10 py-14 lg:py-30">
      {/* Title — left 50% on desktop */}
      <div className="lg:flex-1 lg:w-1/2 flex items-center">
        <h2 className="font-bold text-[28px] lg:text-[40px] leading-[1.4] lg:leading-[1.2] tracking-[-0.6px] text-qb-text-high text-center lg:text-left">
          {block.titleSegments.map((seg, i) => (
            <Fragment key={i}>
              <span className={seg.highlight ? 'text-qb-primary' : ''}>
                {seg.text}
              </span>
              {i < block.titleSegments.length - 1 && <br />}
            </Fragment>
          ))}
        </h2>
      </div>

      {/* Content — right 50% on desktop */}
      <div className="lg:flex-1 lg:w-1/2 flex flex-col gap-10 text-center lg:text-left">
        <p className="font-normal text-[18px] lg:text-[20px] leading-[1.5] tracking-[-0.6px] text-qb-text-mid whitespace-pre-line">
          {block.description}
        </p>
        <p className="font-bold text-[20px] lg:text-[32px] leading-[1.5] lg:leading-[1.4] tracking-[-0.6px] text-qb-text-high whitespace-pre-line">
          {block.closing}
        </p>
      </div>
    </div>
  )
}

export function StorySection({
  blocks,
  imageUrl,
  imageUrlMobile,
  imageAlt,
}: StorySectionProps) {
  const [first, ...rest] = blocks
  const second = rest[0]

  return (
    <section id="story" data-marketing-section="" className="w-full bg-qb-bg">
      {first && <TextBlock block={first} />}

      {/* Full-bleed image band with gradient */}
      <div
        className="w-full overflow-hidden flex justify-center"
        style={{
          background:
            'linear-gradient(to right, rgba(179, 203, 206, 1), rgba(201, 221, 226, 1))',
        }}
      >
        <picture className="flex justify-center">
          <source media="(min-width: 1024px)" srcSet={imageUrl} />
          <img
            src={imageUrlMobile}
            alt={imageAlt}
            className="block h-[320px] lg:h-[440px] object-cover object-center"
            style={{ maxWidth: 'none' }}
          />
        </picture>
      </div>

      {second && <TextBlock block={second} />}
    </section>
  )
}
